/**
 * Individual study row in the study list. Renders both local and Drive-index
 * entries via a unified `StudyListRow` shape. Remote-only rows (no local
 * mirror yet) skip the duplicate/delete actions and the selection checkbox.
 */

import { bibleState } from '@/state/bible-state';
import { TrashIcon, CheckIcon } from '@components/icons';
import { formatReference } from '@skjv/common';
import type { StudyListRow as StudyListRowData } from './group-by-year';

interface StudyRowProps {
  row: StudyListRowData;
  index: number;
  isSelected: boolean;
  isSelecting: boolean;
  onToggleSelection: (id: string, index: number, e: React.MouseEvent) => void;
  onRowClick: (id: string, index: number, e: React.MouseEvent) => void;
}

export function StudyRow({
  row,
  index,
  isSelected,
  isSelecting,
  onToggleSelection,
  onRowClick,
}: StudyRowProps) {
  const isLocal = row.source !== 'remote';
  return (
    <div
      className={`study-row cursor-pointer ${isSelected ? 'ring-2 ring-cyan-400 dark:ring-cyan-500' : ''} ${isLocal ? '' : 'opacity-70'}`}
      onClick={(e) => onRowClick(row.id, index, e)}
    >
      <div className="flex items-start justify-between">
        {isLocal ? (
          <button
            onClick={(e) => onToggleSelection(row.id, index, e)}
            className={`mr-3 mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-colors ${
              isSelected
                ? 'border-cyan-500 bg-cyan-500 text-white'
                : 'border-stone-300 hover:border-cyan-400 dark:border-stone-600'
            }`}
          >
            {isSelected && <CheckIcon className="h-3 w-3" />}
          </button>
        ) : (
          <span
            className="mr-3 mt-1 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-stone-300/60 text-[10px] font-bold text-stone-600 dark:bg-stone-700/60 dark:text-stone-400"
            title="On Google Drive — not yet synced locally"
          >
            ☁
          </span>
        )}

        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-stone-800 dark:text-stone-200">{row.name}</div>
          <div className="mt-1 text-sm text-stone-500 dark:text-stone-400">
            {formatReference(row.startingLocation)}
            {row.endingLocation && ` - ${formatReference(row.endingLocation)}`}
          </div>
          <div className="mt-1 text-xs text-stone-400 dark:text-stone-500">
            {row.studentCount} student{row.studentCount !== 1 ? 's' : ''} &bull;{' '}
            {row.sceneCount} scene{row.sceneCount !== 1 ? 's' : ''}
            {row.hasTeacherNotes && row.sceneCount === 0 ? ' (notes only)' : ''}
          </div>
        </div>

        {isLocal && !isSelecting && row.localStudy && (
          <div className="ml-2 flex gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                bibleState.showConfirmDialog({
                  title: 'Delete Study',
                  message: `Delete "${row.name}"?`,
                  confirmText: 'Delete',
                  variant: 'danger',
                  onConfirm: () => bibleState.removeStudy(row.id),
                });
              }}
              className="p-1.5 text-stone-400 transition-colors hover:text-red-500"
              title="Delete"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
